import NeptuneElement from '../../NeptuneElement.js';

class Slider extends NeptuneElement {

    static SLIDER_TYPES = ['continuous', 'discrete', 'centered'];

    templateMarkup = `
        <section> 
            <span></span>  
            <input type="range"/> 
            <div></div> 
        </section>
    `

    componentCssUrl = '/src/components/form/slider/slider.css';

    constructor() {
        super();
    }

    #onInput = null;
    #onChange = null;

    async connectedCallback() {
        await super.connectedCallback();
        this.rangeInputMin = this.shadowRoot.querySelector('section > input[type="range"]');
        this.tooltipValue = this.shadowRoot.querySelector('span');
        this.dotsContainer = this.shadowRoot.querySelector('section > div');

        this.validateRequiredProperties(['type']);
        this.validatePropertyValue('type', this.getAttribute('type'), Slider.SLIDER_TYPES);

        this.rangeInputMin.min = this.getAttribute('min') || '0';
        this.rangeInputMin.max = this.getAttribute('max') || '100';
        this.rangeInputMin.value = this.getAttribute('value') || this.rangeInputMin.min;

        this.processInitialAttributesAfterRender(this.constructor.observedAttributes);
        
        const type = this.getAttribute('type');
        this.applyType(type);
        this.updateSlider(this.rangeInputMin.value);
        
        if (this.hasAttribute('disabled')) {
            this.updateDisabled();
        }
        
        this.setEventListeners();
    }

    async disconnectedCallback() {
        await super.disconnectedCallback();
    }

    static get observedAttributes() {
        return ["min", "max", "value", "disabled", "type"];
    }

    attributeChangedCallback(name, oldValue, newValue) {
        if (!this.isRendered) {
            return;
        }
        this.processAttribute(name, oldValue, newValue);
    }

    processAttribute(name, oldValue, newValue) {
        switch (name) {
            case "min":
            case "max":
                if (this.rangeInputMin) {
                    this.updateMinMax(name, newValue);
                }
                break;

            case "value":
                this.updateSlider(newValue);
                break;

            case "type":
                this.applyType(newValue);
                break;

            case "disabled":
                if (this.rangeInputMin) {
                    this.updateDisabled();
                }
                break;

            default:
                break;
        }
    }
    updateMinMax(name, newValue) {
        this.rangeInputMin[name] = newValue || (name === "min" ? 0 : 100);
        
        const minValue = parseFloat(this.rangeInputMin.min);
        const maxValue = parseFloat(this.rangeInputMin.max);
        if (minValue >= maxValue) {            
            throw new Error(`[neptune-slider] Invalid range: min (${minValue}) must be less than max (${maxValue})`);
        }
        
        this.applyType(this.getAttribute("type"));
        this.updateSlider(this.rangeInputMin.value);
    }

    updateDisabled() {
        const isDisabled = this.hasAttribute("disabled");
        this.rangeInputMin.disabled = isDisabled;
        this.updateSlider(this.rangeInputMin.value);
    }
    
    setEventListeners() {
        const rangeSliderInput = this.shadowRoot.querySelector('section > input[type="range"]');
        if (!rangeSliderInput || this.#onInput || this.#onChange) {
            return;
        }

        this.#onInput = this.handleInput;
        this.#onChange = (event) => {
            const value = String(event.target.value ?? '');
            this.updateSlider(value);
            if (this.getAttribute('value') !== value) {
                this.setAttribute('value', value);
            }
            this.dispatchEvent(new CustomEvent('change', {
                detail: { value },
                bubbles: true,
                composed: true
            }));
        };

        rangeSliderInput.addEventListener('input', this.#onInput);
        rangeSliderInput.addEventListener('change', this.#onChange);
    }

    removeEventListeners() {
        const rangeSliderInput = this.shadowRoot.querySelector('section > input[type="range"]');
        if (!rangeSliderInput) {
            return;
        }

        if (this.#onInput) {
            rangeSliderInput.removeEventListener('input', this.#onInput);
        }
        if (this.#onChange) {
            rangeSliderInput.removeEventListener('change', this.#onChange);
        }

        this.#onInput = null;
        this.#onChange = null;
    }

    applyStepAttribute(slider) {
        const step = this.getAttribute("step");
        if (step !== null) {
            slider.step = parseFloat(step);
        } else {
            slider.removeAttribute("step");
        }
    }

    applyType(type) {
        if (!this.rangeInputMin) return;

        const slider = this.rangeInputMin;
        this.clearDots();

        switch (type) {
            case "continuous": {
                this.applyStepAttribute(slider);
                this.updateSlider(slider.value);
                break;
            }
            case "discrete": {
                const step = parseInt(this.getAttribute("step") || 10, 10);
                slider.step = step;
                const min = parseFloat(slider.min) || 0;
                const max = parseFloat(slider.max) || 100;
                const dotCount = Math.round((max - min) / step) + 1;
                this.renderDots(dotCount);
                this.updateSlider(slider.value);
                break;
            }
            case "centered": {
                this.applyStepAttribute(slider);
                this.renderDots(3);
                this.updateSlider(slider.value);
                break;
            }
            default:
                break;
        }
    }

    handleInput = (event) => {
        const value = String(event.target.value ?? '');
        this.updateSlider(value);
        if (this.getAttribute('value') !== value) {
            this.setAttribute('value', value);
        }
    };

    clearDots() {
        this.dotsContainer.innerHTML = '';
    }

    renderDots(count) {
        this.dotsContainer.style.setProperty('--dot-count', count - 1);
        
        for (let i = 0; i < count; i++) {
            const dot = document.createElement("span");
            dot.style.setProperty('--dot-index', i);
            this.dotsContainer.appendChild(dot);
        }
    }

    updateSlider(value) {
        if (!this.rangeInputMin) return;
        const slider = this.rangeInputMin;
        slider.value = value;

        const sliderMinBound = parseFloat(slider.min) || 0;
        const sliderMaxBound = parseFloat(slider.max) || 100;
        const progress = ((parseFloat(slider.value) - sliderMinBound) / (sliderMaxBound - sliderMinBound)) * 100;

        this.rangeInputMin.style.setProperty('--progress', `${progress}%`);

        if (this.hasAttribute("tooltip")) {
            this.updateTooltip(slider.value);
        } else {
            this.tooltipValue.textContent = "";
        }
    }

    updateTooltip(value) {
        const slider = this.rangeInputMin;
        this.tooltipValue.innerHTML = value;
        
        const percent = ((slider.value - slider.min) * 100) / (slider.max - slider.min);
        const thumbWidth = 16;
        const trackWidth = slider.offsetWidth;
        
        const thumbPosition = (thumbWidth / 2) + (percent / 100) * (trackWidth - thumbWidth);
        
        this.tooltipValue.style.left = `${thumbPosition}px`;
    }
}

customElements.define("neptune-slider", Slider);

export default Slider;